import React, { useState, createContext, useContext, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AccessGate from './pages/AccessGate';
import PitchDeck from './pages/PitchDeck';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

// Context for access state
const AccessContext = createContext();

export const useAccess = () => useContext(AccessContext);

// Generate session ID
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

function App() {
  const [hasAccess, setHasAccess] = useState(false);
  const [accessCode, setAccessCode] = useState(null);
  const [accessName, setAccessName] = useState(null);
  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem('korefolio_session');
    if (stored) return stored;
    const newId = generateSessionId();
    sessionStorage.setItem('korefolio_session', newId);
    return newId;
  });

  // Check for existing access
  useEffect(() => {
    const storedAccess = sessionStorage.getItem('korefolio_access');
    if (storedAccess) {
      try {
        const { code, name, expiry } = JSON.parse(storedAccess);
        if (expiry > Date.now()) {
          setHasAccess(true);
          setAccessCode(code);
          setAccessName(name);
        } else {
          sessionStorage.removeItem('korefolio_access');
        }
      } catch (e) {
        sessionStorage.removeItem('korefolio_access');
      }
    }
  }, []);

  const grantAccess = (code, name) => {
    setHasAccess(true);
    setAccessCode(code);
    setAccessName(name);
    // Store for 24 hours
    sessionStorage.setItem('korefolio_access', JSON.stringify({
      code,
      name,
      expiry: Date.now() + 24 * 60 * 60 * 1000
    }));
  };

  const revokeAccess = () => {
    setHasAccess(false);
    setAccessCode(null);
    setAccessName(null);
    sessionStorage.removeItem('korefolio_access');
  };

  return (
    <AccessContext.Provider value={{ 
      hasAccess, 
      accessCode, 
      accessName, 
      sessionId,
      grantAccess, 
      revokeAccess 
    }}>
      <div className="min-h-screen bg-surface-dark">
        <Routes>
          <Route 
            path="/" 
            element={
              hasAccess ? <Navigate to="/deck" replace /> : <AccessGate />
            } 
          />
          <Route 
            path="/deck" 
            element={
              hasAccess ? <PitchDeck /> : <Navigate to="/" replace />
            } 
          />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </div>
    </AccessContext.Provider>
  );
}

export default App;
